import type { QueryClient } from "@tanstack/react-query";
import {
  Link,
  Navigate,
  Outlet,
  createRootRouteWithContext,
  createRoute,
  createRouter,
} from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { Alert, Spinner } from "@/components/ui";
import { AcceptInvitePage } from "@/features/access/AcceptInvitePage";
import { NoAccessPage, SelectCompanyPage } from "@/features/access/AccessPages";
import { AuditPage } from "@/features/admin/AuditPage";
import { CompanyPage } from "@/features/admin/CompanyPage";
import { InvitationsPage } from "@/features/admin/InvitationsPage";
import { RolesPage } from "@/features/admin/RolesPage";
import { UsersPage } from "@/features/admin/UsersPage";
import { LoginPage } from "@/features/auth/LoginPage";
import { SetPasswordPage } from "@/features/auth/SetPasswordPage";
import { ChangePasswordPage } from "@/features/auth/ChangePasswordPage";
import { HomePage } from "@/features/inicio/HomePage";
import { PartiesPage } from "@/features/cadastros/PartiesPage";
import { PaymentMethodsPage } from "@/features/cadastros/PaymentMethodsPage";
import { PriceListsPage } from "@/features/cadastros/PriceListsPage";
import { ProductsPage } from "@/features/cadastros/ProductsPage";
import { SalespeoplePage } from "@/features/cadastros/SalespeoplePage";
import { UnitsPage } from "@/features/cadastros/UnitsPage";
import { EntradasPage } from "@/features/compras/EntradasPage";
import { VinculosPage } from "@/features/compras/VinculosPage";
import { ContasPagarPage } from "@/features/financeiro/ContasPagarPage";
import { ContasReceberPage } from "@/features/financeiro/ContasReceberPage";
import { AjustesPage, DepositosPage } from "@/features/estoque/AjustesPage";
import { TransferenciasPage } from "@/features/estoque/TransferenciasPage";
import { CargaPage } from "@/features/cargas/CargaPage";
import { CargasPage } from "@/features/cargas/CargasPage";
import { InventariosPage } from "@/features/estoque/InventariosPage";
import { LotesPage, MovimentosPage, SaldosPage } from "@/features/estoque/SaldosPage";
import { PlatformPage } from "@/features/platform/PlatformPage";
import { PedidosPage } from "@/features/vendas/PedidosPage";
import { VendaDiretaPage } from "@/features/vendas/VendaDiretaPage";
import { PendenciasPage } from "@/features/estoque/PendenciasPage";
import { AcompanhamentoPage } from "@/features/fiscal/AcompanhamentoPage";
import { PendenciasFiscaisPage } from "@/features/fiscal/PendenciasFiscaisPage";
import { FichasPage } from "@/features/producao/FichasPage";
import { OrdensPage } from "@/features/producao/OrdensPage";
import { ConfiguracoesPage } from "@/features/fiscal/ConfiguracoesPage";
import { DevolucoesPage } from "@/features/fiscal/DevolucoesPage";
import { DocumentosPage } from "@/features/fiscal/DocumentosPage";
import { RegrasPage } from "@/features/fiscal/RegrasPage";
import { RotaRoute } from "@/features/rota/RotaRoute";
import { SimuladorPage } from "@/features/fiscal/SimuladorPage";
import { ApiRequestError, errorMessage } from "@/lib/api";
import { setActiveCompanyId } from "@/lib/company-store";
import { LOGIN_ERROR, type PermissionCode } from "@cafegestor/shared";
import { AppShell } from "./AppShell";
import { useAuth } from "./auth";
import { useActiveCompanyId, useCan, useMe } from "./me";

type RouterContext = { queryClient: QueryClient };

/** Exige login. */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === "loading") return <Spinner />;
  if (status === "signed_out") return <Navigate to="/login" />;
  return <>{children}</>;
}

/** A senha mudou depois deste login (troca ou redefinição): sai e volta para o login. */
function SessionEnded() {
  const { signOut } = useAuth();
  const [done, setDone] = useState(false);
  useEffect(() => {
    void signOut().then(() => setDone(true));
  }, [signOut]);
  return done ? <Navigate to="/login" /> : <Spinner />;
}

/**
 * Área interna: exige login e define a empresa ativa.
 * Sem vínculo → "sem acesso" (exceto administração da plataforma).
 */
function AppLayout() {
  const { data: me, isPending, error } = useMe();
  const { needsPassword } = useAuth();
  const active = useActiveCompanyId();
  const companies = me?.companies ?? [];
  const single = companies.length === 1 ? companies[0] : undefined;

  // Com uma única empresa, ela é escolhida automaticamente
  useEffect(() => {
    if (single && active !== single.id) setActiveCompanyId(single.id);
  }, [single, active]);

  if (isPending) return <Spinner />;
  if (error instanceof ApiRequestError && error.code === LOGIN_ERROR.sessionEnded)
    return <SessionEnded />;
  if (error)
    return (
      <div className="p-8">
        <Alert tone="error">{errorMessage(error)}</Alert>
      </div>
    );
  if (needsPassword) return <Navigate to="/definir-senha" />;
  // senha inicial ou redefinida pelo administrador: troca antes de qualquer outra tela
  if (me?.user.mustChangePassword) return <ChangePasswordPage forced />;
  if (companies.length === 0 && !me?.user.isPlatformAdmin) return <Navigate to="/sem-acesso" />;
  if (companies.length > 1 && !companies.some((c) => c.id === active))
    return <Navigate to="/selecionar-empresa" />;
  return <AppShell />;
}

/** Bloqueia a tela (só visual) quando falta permissão; a API também recusa. */
function RequirePermission({ code, children }: { code: PermissionCode; children: ReactNode }) {
  const can = useCan();
  const { data: me } = useMe();
  if (!me?.activeCompany) return <Alert tone="warn">Selecione uma empresa.</Alert>;
  if (!can(code)) return <Alert tone="warn">Você não tem permissão para acessar esta tela.</Alert>;
  return <>{children}</>;
}

function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  if (!me?.user.isPlatformAdmin)
    return <Alert tone="warn">Área exclusiva da administração da plataforma.</Alert>;
  return <>{children}</>;
}

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="py-16 text-center">
      <h1 className="text-2xl font-semibold">Página não encontrada</h1>
      <Link to="/" className="mt-4 inline-block text-primary underline">
        Voltar ao início
      </Link>
    </div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});
const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/definir-senha",
  component: SetPasswordPage,
});
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/convite",
  validateSearch: (s: Record<string, unknown>) => ({
    token: typeof s["token"] === "string" ? s["token"] : "",
  }),
  component: function InviteRoute() {
    const { token } = inviteRoute.useSearch();
    return <AcceptInvitePage token={token} />;
  },
});
const noAccessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/sem-acesso",
  component: () => (
    <RequireAuth>
      <NoAccessPage />
    </RequireAuth>
  ),
});
const selectCompanyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/selecionar-empresa",
  component: () => (
    <RequireAuth>
      <SelectCompanyPage />
    </RequireAuth>
  ),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  component: () => (
    <RequireAuth>
      <AppLayout />
    </RequireAuth>
  ),
});

const page = <P extends string>(path: P, element: () => ReactNode) =>
  createRoute({ getParentRoute: () => appRoute, path, component: element });

const homeRoute = page("/", () => <HomePage />);
const myPasswordRoute = page("/minha-senha", () => <ChangePasswordPage forced={false} />);
const companyRoute = page("/admin/empresa", () => (
  <RequirePermission code="admin.empresa.ver">
    <CompanyPage />
  </RequirePermission>
));
const usersRoute = page("/admin/usuarios", () => (
  <RequirePermission code="admin.usuarios.ver">
    <UsersPage />
  </RequirePermission>
));
const invitationsRoute = page("/admin/convites", () => (
  <RequirePermission code="admin.usuarios.convidar">
    <InvitationsPage />
  </RequirePermission>
));
const rolesRoute = page("/admin/papeis", () => (
  <RequirePermission code="admin.usuarios.ver">
    <RolesPage />
  </RequirePermission>
));
const auditRoute = page("/admin/auditoria", () => (
  <RequirePermission code="admin.auditoria.ver">
    <AuditPage />
  </RequirePermission>
));
const platformRoute = page("/plataforma", () => (
  <RequirePlatformAdmin>
    <PlatformPage />
  </RequirePlatformAdmin>
));

// Cadastros (Fase 2): ver exige cadastros.ver; cada ação confere a própria permissão na API
const cadastro = <P extends string>(path: P, element: () => ReactNode) =>
  page(path, () => <RequirePermission code="cadastros.ver">{element()}</RequirePermission>);
const clientsRoute = cadastro("/cadastros/clientes", () => <PartiesPage papel="cliente" />);
const suppliersRoute = cadastro("/cadastros/fornecedores", () => (
  <PartiesPage papel="fornecedor" />
));
const salespeopleRoute = cadastro("/cadastros/vendedores", () => <SalespeoplePage />);
const productsRoute = cadastro("/cadastros/produtos", () => <ProductsPage />);
const unitsRoute = cadastro("/cadastros/unidades", () => <UnitsPage />);
const paymentMethodsRoute = cadastro("/cadastros/formas-pagamento", () => <PaymentMethodsPage />);
const priceListsRoute = cadastro("/cadastros/tabelas-preco", () => <PriceListsPage />);

// Estoque e produção (Fase 3)
const guarded = <P extends string>(path: P, code: PermissionCode, element: () => ReactNode) =>
  page(path, () => <RequirePermission code={code}>{element()}</RequirePermission>);
const stockRoutes = [
  guarded("/estoque/saldos", "estoque.ver", () => <SaldosPage />),
  guarded("/estoque/movimentacoes", "estoque.ver", () => <MovimentosPage />),
  guarded("/estoque/lotes", "estoque.ver", () => <LotesPage />),
  guarded("/estoque/ajustes", "estoque.ver", () => <AjustesPage />),
  guarded("/estoque/inventarios", "estoque.ver", () => <InventariosPage />),
  guarded("/estoque/depositos", "estoque.ver", () => <DepositosPage />),
  guarded("/estoque/transferencias", "estoque.ver", () => <TransferenciasPage />),
  guarded("/producao/ordens", "producao.ver", () => <OrdensPage />),
  guarded("/producao/fichas", "producao.ver", () => <FichasPage />),
] as const;

// Compras e contas a pagar (Fase 4)
const purchaseRoutes = [
  guarded("/compras/entradas", "compras.ver", () => <EntradasPage />),
  guarded("/compras/vinculos", "compras.ver", () => <VinculosPage />),
  guarded("/financeiro/contas-a-pagar", "financeiro.ver", () => <ContasPagarPage />),
] as const;

// Vendas e contas a receber (Fase 5)
const salesRoutes = [
  guarded("/vendas/pedidos", "vendas.ver", () => <PedidosPage />),
  // v7: a página confere também confirmar e emitir (e explica o que falta)
  guarded("/vendas/venda-direta", "vendas.pedido.criar", () => <VendaDiretaPage />),
  guarded("/estoque/pendencias", "estoque.ver", () => <PendenciasPage />),
  guarded("/financeiro/contas-a-receber", "financeiro.ver", () => <ContasReceberPage />),
] as const;

// Cargas de vendedor e acerto (Fase 6)
function LoadDetailPage(): ReactNode {
  const { id } = loadDetailRoute.useParams();
  return (
    <RequirePermission code="cargas.ver">
      <CargaPage key={id} id={id} />
    </RequirePermission>
  );
}
// página própria da carga: o acerto em três etapas (link direto, voltar do navegador)
const loadDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/cargas/$id",
  component: LoadDetailPage,
});
const loadRoutes = [
  guarded("/cargas", "cargas.ver", () => <CargasPage />),
  loadDetailRoute,
] as const;

// Fiscal (Fase 7)
const fiscalRoutes = [
  guarded("/fiscal/documentos", "fiscal.ver", () => <DocumentosPage />),
  guarded("/fiscal/acompanhamento", "fiscal.ver", () => <AcompanhamentoPage />),
  guarded("/fiscal/pendencias", "fiscal.ver", () => <PendenciasFiscaisPage />),
  guarded("/fiscal/simulador", "fiscal.ver", () => <SimuladorPage />),
  guarded("/fiscal/regras", "fiscal.ver", () => <RegrasPage />),
  guarded("/fiscal/configuracoes", "fiscal.ver", () => <ConfiguracoesPage />),
  guarded("/rota", "vendas.pedido.criar", () => <RotaRoute />),
  guarded("/vendas/devolucoes", "vendas.ver", () => <DevolucoesPage />),
] as const;

const routeTree = rootRoute.addChildren([
  loginRoute,
  setPasswordRoute,
  inviteRoute,
  noAccessRoute,
  selectCompanyRoute,
  appRoute.addChildren([
    homeRoute,
    myPasswordRoute,
    companyRoute,
    usersRoute,
    invitationsRoute,
    rolesRoute,
    auditRoute,
    platformRoute,
    clientsRoute,
    suppliersRoute,
    salespeopleRoute,
    productsRoute,
    unitsRoute,
    paymentMethodsRoute,
    priceListsRoute,
    ...stockRoutes,
    ...purchaseRoutes,
    ...salesRoutes,
    ...loadRoutes,
    ...fiscalRoutes,
  ]),
]);

export function createAppRouter(queryClient: QueryClient) {
  return createRouter({ routeTree, context: { queryClient }, defaultPreload: "intent" });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
